/**
 * Which language the product speaks to a client in.
 *
 * One decision, in one place, because three things have to agree about it and
 * two of them used to disagree silently: the words on the screen
 * (`src/shared/text`), the format of every date (`src/shared/formatDate`), and
 * the `lang` attribute of the served document. Before this module the app was
 * hard-coded English strings inside `<html lang="en">` with an `es-ES` date
 * layer bolted underneath — three answers to one question.
 *
 * **Castilian only, decided by the project owner on 2026-09-14**, in his words:
 * *"let's start with Castilian only, yes."* So `SUPPORTED_LANGUAGES` has exactly
 * one entry, there is deliberately **no language switcher**, and there is no
 * second catalogue of strings. What this module exists to prevent is the other
 * thing he asked for in the same breath: that adding a second language later is
 * not a rewrite.
 *
 * That is why the language is a *type* rather than a string. Adding `'ca'` here
 * makes `Catalogue` demand a complete Catalan catalogue, and `tsc` names every
 * missing entry. Counsel's position (`docs/decisions/spanish-jurisdiction.md`
 * §4.4) is that Catalan may well bind, at low confidence; this is what makes
 * acting on that answer cheap whenever it arrives.
 *
 * The language is a property of the **product**, never of the reader's machine.
 * Nothing here reads `navigator.language`: what a client is shown is a decision
 * the coach and the product made, and a consent record has to be able to name
 * it (`client-language`, "The consent record carries the language and the
 * version of what was read").
 */

/** Every language a client can read the whole product in. */
export const SUPPORTED_LANGUAGES = ['es'] as const;

export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * The floor. A client always has at least this, and no screen may be available
 * in some other language but not this one.
 */
export const FLOOR_LANGUAGE: Language = 'es';

export function isSupportedLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * The language this render is in.
 *
 * Today the body is a constant, because there is one supported language and no
 * switcher. It is a function rather than a constant so that the day an instance
 * or a client carries a language, exactly this body changes and no caller does.
 */
export function resolveLanguage(): Language {
  return FLOOR_LANGUAGE;
}

/**
 * The BCP 47 tag for a language, which is what `Intl` wants and what belongs in
 * `<html lang>`. A language is not a locale: `es` is the language, `es-ES` the
 * regional formatting conventions that go with it here.
 */
const TAGS: Record<Language, string> = {
  es: 'es-ES',
};

export function languageTag(language: Language = resolveLanguage()): string {
  return TAGS[language];
}
